import React, { useState, useEffect } from 'react';
import { useLoaderData, LoaderFunctionArgs } from 'react-router';
import { apiFetch } from '../../../lib/api-client';
import { redirect } from 'react-router';
import { authClient } from '../../../lib/auth-client';
import { useOnboardingForm } from '../../hooks/useOnboardingForm';
import { useResumeStatus } from '../../hooks/useResumeStatus';
import { Button } from '../../../components/ui/Button';
import { Card, useToastActions } from '../../../components/ui';
import { ResumeParseStatus } from '../../components/ResumeParseStatus';
import { ResumeTextPreview } from '../../components/ResumeTextPreview';
import { Upload, FileText, ArrowLeft, Clock } from 'lucide-react';

export async function loader({ request }: LoaderFunctionArgs) {
  try {
    const { data } = await authClient.getSession();
    
    if (!data?.user) {
      const url = new URL(request.url);
      throw redirect(`/login?from=${encodeURIComponent(url.pathname)}`);
    }
    
    // Try to get resumes data
    try {
      const resumesResponse = await fetch('https://api.nduka.io/api/v2/resumes', {
        method: 'GET',
        credentials: 'include',
      });
      
      if (resumesResponse.ok) {
        const resumesData = await resumesResponse.json();
        return { resumes: resumesData.data };
      }
    } catch (error) {
      // Resumes fetch failed, but user is authenticated
    }
    
    return { resumes: [] };
  } catch (error) {
    if (error instanceof Response) throw error;
    const url = new URL(request.url);
    throw redirect(`/login?from=${encodeURIComponent(url.pathname)}`);
  }
}

// Remove action - we'll handle upload programmatically

export function OnboardingResumePage() {
  const { resumes } = useLoaderData() as any;
  const { executeAsync, navigate } = useOnboardingForm({
    nextRoute: '/onboarding/preferences',
    successMessage: 'Resume uploaded successfully!'
  });
  const { success: showSuccess, error: showError } = useToastActions();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [resumeName, setResumeName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedResumeId, setUploadedResumeId] = useState<string | null>(null);
  
  // Use resume status hook for polling
  const { 
    resume, 
    text, 
    isPolling, 
    error: statusError,
    isParseComplete,
    isParseFailed,
    refetch 
  } = useResumeStatus({
    resumeId: uploadedResumeId,
    onParseComplete: (resumeText) => {
      console.log('Parse completed:', resumeText);
      showSuccess('Parse Complete', 'Your resume has been successfully processed!');
    }
  });

  // Handle status errors from useResumeStatus
  useEffect(() => {
    if (statusError) {
      showError('Resume Processing Error', statusError);
    }
    if (isParseFailed && resume?.status.last_error) {
      showError('Parse Failed', resume.status.last_error);
    }
  }, [statusError, isParseFailed, resume, showError]);

  // Handle file upload and onboarding advance
  const handleUpload = async () => {
    if (!selectedFile) {
      return; // Let the user know via UI that file is required
    }

    setIsUploading(true);

    await executeAsync(async () => {
      // 1. Upload the file using apiFetch for proper CSRF handling
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('name', resumeName || selectedFile.name);
      
      const uploadResponse = await apiFetch('/resumes/upload', {
        method: 'POST',
        body: uploadFormData, // Don't use json option for FormData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ error: { message: 'Upload failed' } }));
        showError('Upload Failed', errorData.error?.message || 'Failed to upload resume');
        return;
      }

      // Extract resume ID from response for polling
      const uploadData = await uploadResponse.json();
      if (uploadData.success && uploadData.data?.resume_id) {
        setUploadedResumeId(uploadData.data.resume_id);
      }

      // Note: Onboarding progress is now tracked through Better Auth user profile
      // No need for separate onboarding advancement API calls
      
      setUploadSuccess(true);
      return 'success';
    });

    setIsUploading(false);
  };

  // Handle retry for failed parsing
  const handleRetry = async () => {
    if (!uploadedResumeId) return;
    
    try {
      // Trigger reparse
      await apiFetch(`/resumes/${uploadedResumeId}/reparse`, {
        method: 'POST'
      });
      
      // Start polling again
      refetch();
    } catch (error) {
      console.error('Failed to retry parse:', error);
      showError('Retry Failed', 'Failed to restart parsing. Please try again.');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        showError('Invalid File Type', 'Please select a PDF, DOC, or DOCX file.');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        showError('File Too Large', 'File size must be less than 10MB.');
        return;
      }
      
      setSelectedFile(file);
      setResumeName(file.name);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        showError('Invalid File Type', 'Please select a PDF, DOC, or DOCX file.');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        showError('File Too Large', 'File size must be less than 10MB.');
        return;
      }
      
      setSelectedFile(file);
      setResumeName(file.name);
    }
  };


  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Upload Your Resume</h1>
        <p className="mt-2 text-muted-foreground">We'll analyze your resume to provide better job matches and extract key information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upload Card - Takes up 2 columns */}
        <div className="lg:col-span-2">
          <Card>
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Upload Resume</h2>
                <p className="text-sm text-muted-foreground mt-1">Select a PDF, DOC, or DOCX file to get started</p>
              </div>

              {/* Dropzone */}
              <div 
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center transition-colors
                  ${dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="space-y-4">
                    <FileText className="mx-auto h-12 w-12 text-primary" />
                    <div className="space-y-2">
                      <h3 className="font-medium text-foreground">{selectedFile.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Drag and drop your resume here, or
                      </p>
                      <label htmlFor="resume" className="cursor-pointer">
                        <Button type="button" variant="secondary" className="pointer-events-none">
                          Browse Files
                        </Button>
                        <input
                          id="resume"
                          name="resume"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileSelect}
                        />
                      </label>
                      <p className="text-xs text-muted-foreground">
                        PDF, DOC, or DOCX up to 10MB
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {selectedFile && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Resume Name (optional)
                  </label>
                  <input
                    type="text"
                    name="resumeName"
                    value={resumeName}
                    onChange={(e) => setResumeName(e.target.value)}
                    placeholder="e.g., Software Engineer Resume 2024"
                    className="flex h-10 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Status/Progress Area */}
        <div className="space-y-6">
          {/* Upload Progress */}
          {!uploadSuccess && (
            <Card>
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Upload Status</h3>
                
                {isUploading ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-primary animate-pulse" />
                      <span className="text-sm text-muted-foreground">Uploading...</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                    </div>
                  </div>
                ) : selectedFile ? (
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Ready to upload</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">No file selected</span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Parse Status - only show after upload */}
          {uploadSuccess && (
            <ResumeParseStatus 
              resume={resume}
              isPolling={isPolling}
              onRetry={handleRetry}
            />
          )}

          {/* Text Preview - only show when parse is complete */}
          {uploadSuccess && text && (
            <ResumeTextPreview 
              text={text}
              isLoading={isPolling && !text}
            />
          )}

          {/* Existing Resumes */}
          {resumes && resumes.length > 0 && !uploadSuccess && (
            <Card>
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Your Resumes</h3>
                <div className="space-y-2">
                  {resumes.slice(0, 3).map((resume: any) => (
                    <div key={resume.id} className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-foreground truncate">{resume.title || 'Untitled'}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(resume.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                  {resumes.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      +{resumes.length - 3} more resumes
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/onboarding/profile')}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        
        <div className="flex gap-3">
          {selectedFile && !isUploading && !uploadSuccess && (
            <Button
              type="button"
              onClick={handleUpload}
              disabled={isUploading}
              className="gap-2"
            >
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload Resume
                </>
              )}
            </Button>
          )}
          
          <Button
            type="button"
            variant={isParseComplete || (resumes && resumes.length > 0) ? "default" : "secondary"}
            disabled={uploadSuccess && isPolling && !isParseFailed}
            onClick={() => {
              if (uploadSuccess && isParseFailed) {
                showSuccess('Continuing', 'You can always re-upload your resume later from the Resume Library.');
              }
              navigate('/onboarding/preferences');
            }}
            className="min-w-[120px]"
          >
            {uploadSuccess ? (
              isParseComplete ? 'Continue' : 
              isParseFailed ? 'Continue Anyway' :
              'Processing...'
            ) : (resumes && resumes.length > 0) ? 'Continue' : 'Skip for Now'}
          </Button>
        </div>
      </div>
    </div>
  );
}